import { ReactNode } from "react";
import styled, { ThemeProvider } from "styled-components";
import Footer, { FooterProps } from "./Footer";
import Header, { HeaderProps } from "./Header";
import GlobalStyle from "@cleverbase-trust-drivers/shared/styles/globalStyles";
import {
  Theme,
  setTheme,
} from "@cleverbase-trust-drivers/shared/styles/Themes";

const AppRoot = styled.div<{ $isDesktop: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: max(850px, 100vh);
  height: auto;
  background-position: top center;
  background-repeat: no-repeat;
  background-size: cover;
  background: ${({ theme }) => theme.backgroundColor};
  position: relative;
  &::before {
    content: "";
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 72px;
    background: ${({ theme }) => theme.header.backgroundColor};
    z-index: 0;
    pointer-events: none;
  }
  @media screen and (min-width: 1025px) {
    background-size: 200vw auto;
  }
  @media screen and (max-width: 1024px) {
    flex-direction: column;
    justify-content: flex-start;
  }

  justify-content: space-between;
  position: relative;
  gap: 0;
  @media screen and (max-width: 1024px) {
    gap: 0;
  }
  > header {
    z-index: 2;
  }
`;

const Main = styled.main`
  width: 100%;
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  z-index: 1;
`;

const ViewBox = styled.div`
  border: ${({ theme }) =>
    theme.viewBox.outlineWidth + " solid" + theme.viewBox.outlineColor};
  background-color: ${({ theme }) => theme.viewBox.backgroundColor};
  height: 640px;
  width: 320px;
  border-radius: 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  box-sizing: border-box;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  position: relative;

  @media screen and (max-width: 480px) {
    border-radius: 0px;
    border: 0px;
    border-top: ${({ theme }) =>
      theme.viewBox.outlineWidth + " solid" + theme.viewBox.outlineColor};
    border-bottom: ${({ theme }) =>
      theme.viewBox.outlineWidth + " solid" + theme.viewBox.outlineColor};
    box-sizing: none;
    box-shadow: none;
  }
  width: min(480px, calc(100vw - 64px));
  max-width: 480px;
  height: auto;
  max-height: none;
  min-width: 0;
  border: 0;
  border-radius: 16px;
  padding: 40px;
  gap: 24px;
  justify-content: flex-start;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
  position: relative;
  margin-left: auto;
  margin-right: auto;

  @media screen and (max-width: 480px) {
    border-radius: 16px;
    padding: 24px;
    height: auto;
    position: relative;
    transform: none;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
  }
`;

const FooterWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  z-index: 2;
`;

export interface AppRootProps {
  header: HeaderProps;
  children?: ReactNode;
  footer: FooterProps;
  isDesktop: boolean;
  theme: Theme;
}

export default function App(props: AppRootProps): JSX.Element {
  return (
    <ThemeProvider theme={setTheme(props.theme)}>
      <AppRoot $isDesktop={props.isDesktop}>
        <GlobalStyle />
        <Header
          supportedLocales={props.header.supportedLocales}
          setLanguage={props.header.setLanguage}
          currentLanguage={props.header.currentLanguage}
          isDesktop={props.isDesktop}
          aboutText={props.header.aboutText}
          registerText={props.header.registerText}
          supportText={props.header.supportText}
          aboutUrl={props.header.aboutUrl}
          registerUrl={props.header.registerUrl}
          supportUrl={props.header.supportUrl}
        />
        <Main>
          <ViewBox>{props.children}</ViewBox>
        </Main>
        <FooterWrap>
          <Footer
            helpText={props.footer.helpText}
            registerText={props.footer.registerText}
            aboutText={props.footer.aboutText}
            isDesktop={props.isDesktop}
            helpSteps={props.footer.helpSteps}
            registerUrl={props.footer.registerUrl}
            aboutUrl={props.footer.aboutUrl}
          />
        </FooterWrap>
      </AppRoot>
    </ThemeProvider>
  );
}
